import { useState } from 'react'
import {
  User, Mail, Phone, MapPin, Briefcase, Shield,
  Edit3, Save, X, Lock, Eye, EyeOff, CheckCircle2,
  AlertTriangle, Loader2, KeyRound, ChevronRight
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { authService } from '@/services/firebase/auth'
import toast from 'react-hot-toast'

// ── helpers ──────────────────────────────────────────────────────────────────
const ROLE_CFG: Record<string, { label: string; color: string; bg: string }> = {
  admin:      { label: 'Admin',      color: 'text-violet-700', bg: 'bg-violet-100' },
  engineer:   { label: 'Engineer',   color: 'text-blue-700',   bg: 'bg-blue-100'   },
  supervisor: { label: 'Supervisor', color: 'text-amber-700',  bg: 'bg-amber-100'  },
  customer:   { label: 'Customer',   color: 'text-gray-700',   bg: 'bg-gray-100'   },
}

const initials = (name: string) =>
  name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

const PasswordInput = ({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string
}) => {
  const [show, setShow] = useState(false)
  return (
    <div>
      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full text-sm bg-gray-50 border border-gray-200 rounded-2xl px-3.5 py-3 pr-10 outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-all text-gray-800 placeholder-gray-400"
        />
        <button type="button" onClick={() => setShow(s => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
          {show ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      </div>
    </div>
  )
}

// ── main ─────────────────────────────────────────────────────────────────────
const ProfilePage = () => {
  const { user, updateUser } = useAuthStore()

  // ── Edit profile state ──
  const [editOpen, setEditOpen] = useState(false)
  const [name, setName]               = useState(user?.name ?? '')
  const [phone, setPhone]             = useState(user?.phoneNumber ?? '')
  const [zone, setZone]               = useState(user?.assignedZone ?? '')
  const [empId, setEmpId]             = useState(user?.employeeId ?? '')
  const [savingProfile, setSavingProfile] = useState(false)

  // ── Change email state ──
  const [emailOpen, setEmailOpen] = useState(false)
  const [newEmail, setNewEmail]       = useState('')
  const [emailPwd, setEmailPwd]       = useState('')
  const [savingEmail, setSavingEmail] = useState(false)

  // ── Change password state ──
  const [pwdOpen, setPwdOpen] = useState(false)
  const [currentPwd, setCurrentPwd]   = useState('')
  const [newPwd, setNewPwd]           = useState('')
  const [confirmPwd, setConfirmPwd]   = useState('')
  const [savingPwd, setSavingPwd]     = useState(false)

  if (!user) return null

  const role = ROLE_CFG[user.role] ?? ROLE_CFG.customer
  const hasEmail = !!user.email
  const uid = user.uid ?? user.id

  // ── handlers ──
  const handleSaveProfile = async () => {
    if (!name.trim()) { toast.error('Name is required'); return }
    setSavingProfile(true)
    try {
      await authService.updateUserProfile(uid, {
        name: name.trim(),
        phoneNumber: phone.trim() || undefined,
        assignedZone: zone.trim() || undefined,
        employeeId: empId.trim() || undefined,
      })
      updateUser({ name: name.trim(), phoneNumber: phone.trim(), assignedZone: zone.trim(), employeeId: empId.trim() })
      toast.success('Profile updated')
      setEditOpen(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Update failed')
    }
    setSavingProfile(false)
  }

  const handleChangeEmail = async () => {
    if (!newEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast.error('Enter a valid email address'); return
    }
    if (!emailPwd) { toast.error('Current password is required'); return }
    setSavingEmail(true)
    try {
      await authService.updateUserEmail(uid, newEmail.trim(), emailPwd)
      updateUser({ email: newEmail.trim() })
      toast.success('Email updated successfully')
      setEmailOpen(false)
      setNewEmail(''); setEmailPwd('')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed'
      if (msg.includes('wrong-password') || msg.includes('invalid-credential')) toast.error('Current password is incorrect')
      else if (msg.includes('email-already-in-use')) toast.error('Email already in use')
      else toast.error(msg)
    }
    setSavingEmail(false)
  }

  const handleChangePassword = async () => {
    if (!currentPwd) { toast.error('Enter your current password'); return }
    if (newPwd.length < 6) { toast.error('New password must be at least 6 characters'); return }
    if (newPwd !== confirmPwd) { toast.error('Passwords do not match'); return }
    setSavingPwd(true)
    try {
      await authService.updateUserPassword(newPwd, currentPwd)
      toast.success('Password changed successfully')
      setPwdOpen(false)
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed'
      if (msg.includes('wrong-password') || msg.includes('invalid-credential')) toast.error('Current password is incorrect')
      else toast.error(msg)
    }
    setSavingPwd(false)
  }

  // ── field row helper ──
  const Field = ({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value?: string }) =>
    value ? (
      <div className="flex items-center gap-3 py-3 border-b border-gray-50 last:border-0">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: 'rgba(99,102,241,0.07)' }}>
          <Icon size={14} className="text-indigo-400" />
        </div>
        <div className="min-w-0">
          <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{label}</p>
          <p className="text-sm font-semibold text-gray-800 truncate mt-0.5">{value}</p>
        </div>
      </div>
    ) : null

  return (
    <div className="max-w-xl mx-auto space-y-5 pb-10">

      {/* ── Hero card ── */}
      <div className="relative overflow-hidden rounded-3xl text-white"
        style={{ background: 'linear-gradient(135deg, #050d1f 0%, #0c1e3d 50%, #0d2a50 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.35) 0%, transparent 70%)', filter: 'blur(40px)' }} />
          <div className="absolute bottom-0 left-1/4 w-40 h-28 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.2) 0%, transparent 70%)', filter: 'blur(30px)' }} />
        </div>
        <div className="relative z-10 p-6 flex items-center gap-5">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-3xl flex items-center justify-center text-2xl font-black shrink-0 border-2 border-white/20"
            style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)' }}>
            {initials(user.name)}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-black leading-tight truncate">{user.name}</h2>
            {user.email && <p className="text-blue-300/80 text-sm mt-0.5 truncate">{user.email}</p>}
            {!user.email && user.phoneNumber && <p className="text-blue-300/80 text-sm mt-0.5">{user.phoneNumber}</p>}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`text-[10px] font-black px-2.5 py-1 rounded-full ${role.bg} ${role.color}`}>
                {role.label}
              </span>
              {user.employeeId && (
                <span className="text-[10px] font-semibold text-blue-300/60">ID: {user.employeeId}</span>
              )}
              <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${user.isActive ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${user.isActive ? 'bg-emerald-400' : 'bg-red-400'}`} />
                {user.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Profile details card ── */}
      <div className="bg-white rounded-3xl overflow-hidden"
        style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
          <p className="text-sm font-black text-gray-800">Profile Details</p>
          <button onClick={() => { setEditOpen(o => !o); setName(user.name); setPhone(user.phoneNumber ?? ''); setZone(user.assignedZone ?? ''); setEmpId(user.employeeId ?? '') }}
            className="flex items-center gap-1.5 text-[11px] font-black px-3 py-1.5 rounded-xl transition-all hover:scale-[1.03]"
            style={{ background: editOpen ? 'rgba(239,68,68,0.08)' : 'rgba(99,102,241,0.08)', color: editOpen ? '#ef4444' : '#6366f1' }}>
            {editOpen ? <><X size={12} /> Cancel</> : <><Edit3 size={12} /> Edit</>}
          </button>
        </div>

        {!editOpen ? (
          <div className="px-5">
            <Field icon={User}     label="Full Name"      value={user.name} />
            <Field icon={Mail}     label="Email"          value={user.email} />
            <Field icon={Phone}    label="Phone"          value={user.phoneNumber} />
            <Field icon={MapPin}   label="Assigned Zone"  value={user.assignedZone} />
            <Field icon={Briefcase}label="Employee ID"    value={user.employeeId} />
            <Field icon={Shield}   label="Role"           value={role.label} />
            {user.lastLoginAt && (
              <Field icon={CheckCircle2} label="Last Login"
                value={user.lastLoginAt.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} />
            )}
          </div>
        ) : (
          <div className="px-5 py-4 space-y-4">
            {/* Name */}
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">Full Name *</label>
              <input value={name} onChange={e => setName(e.target.value)}
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-2xl px-3.5 py-3 outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-all text-gray-800"
                placeholder="Your full name" />
            </div>
            {/* Phone */}
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">Phone Number</label>
              <input value={phone} onChange={e => setPhone(e.target.value)}
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-2xl px-3.5 py-3 outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-all text-gray-800"
                placeholder="+91 XXXXXXXXXX" />
            </div>
            {/* Role-specific fields */}
            {(user.role === 'engineer' || user.role === 'admin') && (
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">Assigned Zone</label>
                <input value={zone} onChange={e => setZone(e.target.value)}
                  className="w-full text-sm bg-gray-50 border border-gray-200 rounded-2xl px-3.5 py-3 outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-all text-gray-800"
                  placeholder="e.g. Kozhikode North" />
              </div>
            )}
            {user.role === 'engineer' && (
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">Employee ID</label>
                <input value={empId} onChange={e => setEmpId(e.target.value)}
                  className="w-full text-sm bg-gray-50 border border-gray-200 rounded-2xl px-3.5 py-3 outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-all text-gray-800"
                  placeholder="EMP-001" />
              </div>
            )}
            <button onClick={handleSaveProfile} disabled={savingProfile}
              className="w-full py-3.5 text-white font-black rounded-2xl text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-all hover:scale-[1.01]"
              style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)', boxShadow: '0 4px 16px rgba(139,92,246,0.3)' }}>
              {savingProfile ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              Save Changes
            </button>
          </div>
        )}
      </div>

      {/* ── Security card (email users only) ── */}
      {hasEmail && (
        <div className="bg-white rounded-3xl overflow-hidden"
          style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)' }}>
          <div className="px-5 py-4 border-b border-gray-50">
            <div className="flex items-center gap-2">
              <Lock size={14} className="text-indigo-400" />
              <p className="text-sm font-black text-gray-800">Security</p>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">Manage your login email and password</p>
          </div>

          {/* Change email row */}
          <div className="border-b border-gray-50">
            <button onClick={() => { setEmailOpen(o => !o); setPwdOpen(false) }}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/60 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-blue-50">
                  <Mail size={14} className="text-blue-500" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-gray-800">Change Email</p>
                  <p className="text-xs text-gray-400">{user.email}</p>
                </div>
              </div>
              <ChevronRight size={16} className={`text-gray-300 transition-transform ${emailOpen ? 'rotate-90' : ''}`} />
            </button>
            {emailOpen && (
              <div className="px-5 pb-5 space-y-3 border-t border-gray-50">
                <div className="flex items-center gap-2 p-3 rounded-2xl bg-amber-50 border border-amber-100 mt-3">
                  <AlertTriangle size={13} className="text-amber-500 shrink-0" />
                  <p className="text-xs text-amber-700">You'll need to enter your current password to confirm this change.</p>
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">New Email Address</label>
                  <input value={newEmail} onChange={e => setNewEmail(e.target.value)} type="email"
                    placeholder="new@email.com"
                    className="w-full text-sm bg-gray-50 border border-gray-200 rounded-2xl px-3.5 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all text-gray-800 placeholder-gray-400" />
                </div>
                <PasswordInput label="Current Password" value={emailPwd} onChange={setEmailPwd} placeholder="Enter current password" />
                <button onClick={handleChangeEmail} disabled={savingEmail}
                  className="w-full py-3.5 text-white font-black rounded-2xl text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                  style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', boxShadow: '0 4px 16px rgba(59,130,246,0.3)' }}>
                  {savingEmail ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
                  Update Email
                </button>
              </div>
            )}
          </div>

          {/* Change password row */}
          <div>
            <button onClick={() => { setPwdOpen(o => !o); setEmailOpen(false) }}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/60 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-violet-50">
                  <KeyRound size={14} className="text-violet-500" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-gray-800">Change Password</p>
                  <p className="text-xs text-gray-400">Update your login password</p>
                </div>
              </div>
              <ChevronRight size={16} className={`text-gray-300 transition-transform ${pwdOpen ? 'rotate-90' : ''}`} />
            </button>
            {pwdOpen && (
              <div className="px-5 pb-5 space-y-3 border-t border-gray-50">
                <div className="mt-3">
                  <PasswordInput label="Current Password" value={currentPwd} onChange={setCurrentPwd} placeholder="Enter current password" />
                </div>
                <PasswordInput label="New Password" value={newPwd} onChange={setNewPwd} placeholder="Min. 6 characters" />
                <PasswordInput label="Confirm New Password" value={confirmPwd} onChange={setConfirmPwd} placeholder="Re-enter new password" />
                {newPwd && confirmPwd && newPwd !== confirmPwd && (
                  <p className="text-xs text-red-500 flex items-center gap-1.5">
                    <AlertTriangle size={11} /> Passwords do not match
                  </p>
                )}
                {newPwd && confirmPwd && newPwd === confirmPwd && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1.5">
                    <CheckCircle2 size={11} /> Passwords match
                  </p>
                )}
                <button onClick={handleChangePassword} disabled={savingPwd}
                  className="w-full py-3.5 text-white font-black rounded-2xl text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                  style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)', boxShadow: '0 4px 16px rgba(139,92,246,0.3)' }}>
                  {savingPwd ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
                  Change Password
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Phone-only users: no email security section ── */}
      {!hasEmail && (
        <div className="bg-white rounded-3xl px-5 py-4 flex items-center gap-3"
          style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)' }}>
          <div className="w-9 h-9 rounded-2xl flex items-center justify-center bg-gray-50 shrink-0">
            <Phone size={16} className="text-gray-400" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-700">Phone Login</p>
            <p className="text-xs text-gray-400 mt-0.5">Your account uses OTP login. Password changes are not applicable.</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProfilePage
